import { TRUCK_COST } from "../core/constants";
import {
  daysLeft, disruptionsOn, hasKind, throughputDaysLeft, throughputOn,
} from "../core/disruption";
import { clamp, findNode, nextMilestone, pickTarget, regionExpansionCost, routeLabel } from "../core/sim";
import type { GameState, MilestoneId, ThroughputDisruption } from "../core/types";
import { invalidatePalette } from "../render/palette";
import { coarsePointer, euro, need, pct } from "./format";
import { entryFor, MILESTONE_TEXT, manualEntries } from "./milestones";
import type { UiState } from "./state";

type Theme = "light" | "dark";
const THEME_KEY = "umschlag.theme";

export class Hud {
  private money = need("stat-money");
  private day = need("stat-day");
  private fleet = need("stat-fleet");
  private goal = need("stat-goal");
  private goalLabel = need("stat-goal-label");
  private statusbar = need("statusbar");
  private overlay = need("overlay");
  private overlayTitle = need("overlay-title");
  private overlayText = need("overlay-text");
  private themeBtn = need<HTMLButtonElement>("theme-toggle");
  private event = need("stat-event");
  private eventValue = need("stat-event-value");
  private manual = need<HTMLDialogElement>("manual");
  private manualTitle = need("manual-title");
  private manualList = need("manual-list");
  private manualEntry = need("manual-entry");
  private manualBack = need<HTMLButtonElement>("manual-back");
  private speedBtns: HTMLButtonElement[];
  /** Speed to restore when the overlay or the manual is dismissed. */
  private resumeSpeed = 1;
  /**
   * What this world has reached, kept from the last sync(). The manual is a view
   * of it, so there is nothing to store and no save version to bump (RFC-0010).
   */
  private reached: readonly MilestoneId[] = [];
  /** What announce() last showed, so „Im Handbuch nachlesen" knows where to go. */
  private announced: MilestoneId | null = null;

  constructor(private readonly ui: UiState) {
    this.speedBtns = [...document.querySelectorAll<HTMLButtonElement>("#speeds button")];
    for (const b of this.speedBtns) {
      b.addEventListener("click", () => this.setSpeed(Number(b.dataset["speed"])));
    }
    this.themeBtn.addEventListener("click", () => this.toggleTheme());
    need("overlay-continue").addEventListener("click", () => this.closeOverlay());

    need("manual-open").addEventListener("click", () => this.openManual());
    need("manual-close").addEventListener("click", () => this.manual.close());
    this.manualBack.addEventListener("click", () => this.showIndex());
    // Dismiss the announcement first: it has been read, and leaving it up behind
    // the dialog would show it again the moment the manual closes.
    need("overlay-manual").addEventListener("click", () => {
      const id = this.announced;
      this.closeOverlay();
      // Straight to the page this announcement is about. Opening the whole
      // manual here was the bug RFC-0013 is mostly about: the one button whose
      // target is unambiguous was the one ignoring it.
      this.openManual(id ? entryFor(id)?.id : undefined);
    });
    // Not while the announcement is still up: that one owns resumeSpeed until it
    // is dismissed, and resuming here would run the world behind an open overlay.
    this.manual.addEventListener("close", () => {
      if (this.overlay.dataset["open"] !== "true") this.setSpeed(this.resumeSpeed);
    });

    this.applyTheme(this.storedTheme());
  }

  // ── Geschwindigkeit ──────────────────────────────────────────────────────

  setSpeed(v: number): void {
    this.ui.speed = v;
    for (const b of this.speedBtns) {
      b.setAttribute("aria-pressed", String(Number(b.dataset["speed"]) === v));
    }
  }

  togglePause(): void {
    this.setSpeed(this.ui.speed === 0 ? 1 : 0);
  }

  // ── Theme ────────────────────────────────────────────────────────────────

  private storedTheme(): Theme {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === "light" || saved === "dark") return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }

  private applyTheme(theme: Theme): void {
    document.documentElement.dataset["theme"] = theme;
    this.themeBtn.textContent = theme === "light" ? "Dunkel" : "Hell";
    this.themeBtn.setAttribute("aria-pressed", String(theme === "dark"));
    localStorage.setItem(THEME_KEY, theme);
    invalidatePalette(); // sonst zeichnet das Canvas weiter in den alten Farben
  }

  toggleTheme(): void {
    this.applyTheme(document.documentElement.dataset["theme"] === "dark" ? "light" : "dark");
  }

  // ── Pro Frame ────────────────────────────────────────────────────────────

  sync(s: GameState): void {
    this.reached = s.milestones;
    this.money.textContent = euro(s.money);
    this.money.classList.toggle("alarm", s.money < TRUCK_COST * 0.5);
    this.day.textContent = String(s.day);
    this.fleet.textContent = String(s.trucks.length);

    this.syncDisruptions(s);

    // Revenue against the next threshold, not the balance: the bar has to keep
    // rising while the player spends everything on trucks.
    const next = nextMilestone(s);
    const regionsRemain = s.regionCount < 12;
    this.goalLabel.textContent = next
      ? `Meilenstein — ${next.label}`
      : regionsRemain ? `Nächste Region — ${euro(regionExpansionCost(s))}` : "Alle Regionen erschlossen";
    this.goal.style.width = next
      ? `${clamp((s.earned / next.earned) * 100, 0, 100)}%`
      : regionsRemain ? `${clamp((s.money / regionExpansionCost(s)) * 100, 0, 100)}%` : "100%";

    // Naming a key on a device that has none is worse than saying nothing.
    const touch = coarsePointer();
    this.statusbar.innerHTML =
      // The target is picked and the network offered a cheaper path over roads
      // that already exist — the next move is in the panel, not on the map.
      this.ui.linkTo !== null
        ? "<strong>Eigene Strecke</strong> oder mitbenutzen? Im Panel wählen"
        : this.ui.linkFrom !== null
        ? touch
          ? "Ziel-<strong>Stadt</strong> antippen · Ziehen verschiebt · " +
            "freie Fläche bricht ab"
          : "Ziel-<strong>Stadt</strong> anklicken · <strong>Esc</strong> bricht ab"
        : touch
          ? "<strong>Produzent</strong>, dann <strong>Stadt</strong> antippen baut eine Strecke · " +
            "Strecke antippen kauft <strong>Lkw</strong> · Ziehen verschiebt, Aufziehen zoomt"
          : "<strong>Produzent → Stadt</strong> anklicken baut eine Strecke · " +
            "Strecke anklicken kauft <strong>Lkw</strong> (<strong>L</strong>) · " +
            "<strong>Leertaste</strong> pausiert";
  }

  // ── Störungen (RFC-0011) ─────────────────────────────────────────────────

  /**
   * The chip for whatever weather is running. Reads the same pure function the
   * simulation reads — a HUD calling into core is not the upward call rule 1
   * forbids, and deriving it here means there is no third copy of the state.
   */
  private syncDisruptions(s: GameState): void {
    const active = disruptionsOn(s.seed, s.day);
    const hit = throughputOn(s.seed, s.day);
    this.event.hidden = active.length === 0 && hit === null;
    if (this.event.hidden) {
      delete this.event.dataset["kind"];
      return;
    }

    // Durchsatz schlägt Kosten, Teurer schlägt Billiger: laufen mehrere, ist die
    // schwerwiegendste die Farbe — und der Text nennt sie ohnehin alle.
    this.event.dataset["kind"] = hit
      ? "throughput"
      : hasKind(active, "fuel") ? "fuel" : "subsidy";

    const parts = active.map((d) => {
      const left = daysLeft(d, s.day);
      const name = d.kind === "fuel" ? "Treibstoff" : "Ausgleich";
      const sign = d.factor > 1 ? "+" : "−";
      return `${name} ${sign}${pct(Math.abs(d.factor - 1))} Unterhalt · `
        + `${left} ${left === 1 ? "Tag" : "Tage"}`;
    });

    if (hit) {
      const left = throughputDaysLeft(hit, s.day);
      // Named, not "somewhere a producer is down": the whole point of a targeted
      // disruption is that the player can move trucks in response, and they
      // cannot move them towards a rumour.
      const where = this.throughputTarget(s, hit);
      const label = { outage: "Ausfall", slump: "Einbruch", closure: "Sperrung" }[hit.kind];
      const what = { outage: "Produktion", slump: "Bedarf", closure: "Tempo" }[hit.kind];
      parts.unshift(`${label}${where} −${pct(1 - hit.factor)} ${what} · `
        + `${left} ${left === 1 ? "Tag" : "Tage"}`);
    }

    this.eventValue.textContent = parts.join(" · ");
  }

  /**
   * " · Sägewerk Nord" for the chip, or "" when the disruption found nothing —
   * a closure in a world without routes is a no-op, and naming it would mean
   * inventing a target.
   *
   * pickTarget() is asked rather than reimplemented: a second copy of that rule
   * would drift from the one step() applies, and then the chip would blame the
   * wrong sawmill.
   */
  private throughputTarget(s: GameState, hit: ThroughputDisruption): string {
    const { nodeId, routeId } = pickTarget(s, hit);
    if (nodeId !== null) return ` · ${findNode(s, nodeId).name}`;
    // Its ends, not its id: the id is drawn nowhere, so „Strecke 3" named the
    // one disruption the player can answer in terms they cannot look up.
    if (routeId !== null) return ` · ${routeLabel(s, routeId)}`;
    return "";
  }

  // ── Meilenstein-Overlay ──────────────────────────────────────────────────

  /**
   * Announces a milestone and pauses. The map just grew underneath the player,
   * and letting them look at it is the honest thing to do — the same reason the
   * save menu pauses.
   */
  announce(id: MilestoneId): void {
    if (this.overlay.dataset["open"] !== "true") {
      this.resumeSpeed = this.ui.speed;
      this.setSpeed(0);
    }
    this.announced = id;
    this.overlayTitle.textContent = MILESTONE_TEXT[id].title;
    this.overlayText.textContent = MILESTONE_TEXT[id].body;
    this.overlay.dataset["open"] = "true";
  }

  private closeOverlay(): void {
    this.overlay.dataset["open"] = "false";
    this.setSpeed(this.resumeSpeed);
  }

  // ── Handbuch ─────────────────────────────────────────────────────────────

  /**
   * Opens the manual on what this world has reached, and pauses while it is up —
   * the same bargain as the save menu: nobody is playing while they are reading.
   *
   * Built on open, never per frame. That is the trap CLAUDE.md documents for
   * Panel, and the precedent bindSaveMenu() sets.
   */
  openManual(entryId?: string): void {
    // Idempotent since RFC-0013: called on an open dialog it changes the view and
    // nothing else. showModal() throws on one that is already open, and — the
    // quieter half — the speed captured below would be the 0 this method itself
    // set, so the world would stay paused after closing.
    const fresh = !this.manual.open;
    if (fresh) {
      // The announcement owns resumeSpeed while it is up; overwriting it here
      // with the 0 it just set would leave the world paused after both are
      // closed.
      if (this.overlay.dataset["open"] !== "true") this.resumeSpeed = this.ui.speed;
      this.setSpeed(0);
    }

    if (entryId) this.showEntry(entryId);
    else this.showIndex();
    if (fresh) this.manual.showModal();
  }

  /**
   * The list of what this world may read. Built here, never per frame — that is
   * the trap CLAUDE.md documents for Panel: rebuilt markup takes the click
   * handlers with it, and here they are the whole feature.
   */
  private showIndex(): void {
    const focused = this.manualEntry.dataset["entry"];

    this.manualTitle.textContent = "Handbuch";
    this.manualList.innerHTML = manualEntries(this.reached)
      .map((e) => `
        <button type="button" class="manual-link" data-entry="${e.id}">
          ${e.title}
        </button>`)
      .join("");
    for (const b of this.manualList.querySelectorAll<HTMLButtonElement>(".manual-link")) {
      b.addEventListener("click", () => this.showEntry(b.dataset["entry"] ?? ""));
    }

    this.manualList.hidden = false;
    this.manualEntry.hidden = true;
    this.manualBack.hidden = true;

    // Back onto the entry that was just read, so returning does not cost a
    // keyboard user their place. On first open there is none, and the first
    // entry is the honest starting point.
    const back = focused
      ? this.manualList.querySelector<HTMLButtonElement>(`[data-entry="${focused}"]`)
      : null;
    (back ?? this.manualList.querySelector<HTMLButtonElement>(".manual-link"))?.focus();
  }

  /**
   * One page. Looked up in manualEntries(), never in MANUAL: that keeps the
   * milestone filter the only filter, so no path — not a stale id, not a call
   * from the announcement — can open something this world has not unlocked.
   * An unknown id falls back to the list rather than to an empty page.
   */
  private showEntry(id: string): void {
    const entry = manualEntries(this.reached).find((e) => e.id === id);
    if (!entry) {
      this.showIndex();
      return;
    }

    this.manualTitle.textContent = entry.title;
    // Authored copy, not player input: the <strong> in it is meant to render, and
    // nothing from a save reaches this string.
    this.manualEntry.innerHTML = `<p class="hint-body">${entry.body}</p>`;
    this.manualEntry.dataset["entry"] = entry.id;

    this.manualList.hidden = true;
    this.manualEntry.hidden = false;
    this.manualBack.hidden = false;
    this.manualBack.focus();
  }
}
